/**
 * Payload API methods for DEX writer workflow.
 * Handles fetching reader public keys and uploading encrypted payloads.
 */

import { api } from './client.js';

/**
 * Get a reader's public key for encryption.
 *
 * @param {string} readerId - The reader's party ID
 * @returns {Promise<{party_id: string, public_key: string}>} Reader info with base64 public key
 * @throws {ApiError} If reader not found or unauthorized
 */
export async function getReaderPublicKey(readerId) {
  const path = `/parties/${encodeURIComponent(readerId)}/public-key`;
  return api.get(path);
}

/**
 * Upload an encrypted payload for a reader.
 *
 * @param {Object} params - Payload parameters
 * @param {string} [params.workflowId] - Optional workflow ID (omit for "any" workflow)
 * @param {string} params.sampleId - Sample identifier
 * @param {string} params.stageId - Stage/step identifier
 * @param {string} params.readerId - Target reader's party ID
 * @param {string} params.payload - Encrypted payload (base64)
 * @param {number} [params.ttlSeconds] - Optional TTL in seconds
 * @returns {Promise<Object>} API response with payload metadata
 * @throws {ApiError} If upload fails
 */
export async function putPayload({ workflowId, sampleId, stageId, readerId, payload, ttlSeconds }) {
  // Build path: 3-param if workflowId provided, 2-param otherwise
  let path;
  if (workflowId) {
    path = `/payloads/${encodeURIComponent(workflowId)}/${encodeURIComponent(sampleId)}/${encodeURIComponent(stageId)}`;
  } else {
    path = `/payloads/${encodeURIComponent(sampleId)}/${encodeURIComponent(stageId)}`;
  }

  // Build request body
  const body = {
    reader_id: readerId,
    payload
  };

  // Add optional TTL
  if (ttlSeconds !== undefined && ttlSeconds !== null) {
    body.ttl_seconds = ttlSeconds;
  }

  return api.put(path, body);
}

/**
 * Get a payload by its identifiers.
 *
 * @param {string} workflowId - Workflow ID ('any' for wildcard)
 * @param {string} sampleId - Sample identifier
 * @param {string} stageId - Stage/step identifier
 * @returns {Promise<Object>} Payload object with metadata and encrypted content
 * @throws {ApiError} If payload not found or unauthorized
 */
export async function getPayload(workflowId, sampleId, stageId) {
  // Build path: 2-param if 'any' or falsy workflow, 3-param otherwise
  let path;
  if (!workflowId || workflowId === 'any') {
    path = `/payloads/${encodeURIComponent(sampleId)}/${encodeURIComponent(stageId)}`;
  } else {
    path = `/payloads/${encodeURIComponent(workflowId)}/${encodeURIComponent(sampleId)}/${encodeURIComponent(stageId)}`;
  }

  return api.get(path);
}

/**
 * Mark a payload as complete (reader has processed it).
 *
 * @param {string} workflowId - Workflow ID ('any' for wildcard)
 * @param {string} sampleId - Sample identifier
 * @param {string} stageId - Stage/step identifier
 * @returns {Promise<Object>} API response confirming completion
 * @throws {ApiError} If payload not found or unauthorized
 */
export async function markPayloadComplete(workflowId, sampleId, stageId) {
  // Build path: same logic as getPayload but with /complete suffix
  let path;
  if (!workflowId || workflowId === 'any') {
    path = `/payloads/${encodeURIComponent(sampleId)}/${encodeURIComponent(stageId)}/complete`;
  } else {
    path = `/payloads/${encodeURIComponent(workflowId)}/${encodeURIComponent(sampleId)}/${encodeURIComponent(stageId)}/complete`;
  }

  return api.post(path, {});
}

/**
 * Parse a payload partition key into its components.
 *
 * @param {string} pk - Partition key in format 'workflow_id#sample_id#stage_id'
 * @returns {{workflow_id: string, sample_id: string, stage_id: string}} Parsed components
 * @throws {Error} If pk format is invalid
 */
export function parsePayloadPK(pk) {
  if (pk == null || typeof pk !== 'string' || pk.length === 0) {
    throw new Error('Invalid payload PK: expected non-empty string');
  }
  const parts = pk.split('#');
  if (parts.length !== 3) {
    throw new Error(`Invalid payload PK format: expected 3 parts, got ${parts.length}`);
  }
  // Validate each component is a non-empty string
  const [workflow_id, sample_id, stage_id] = parts;
  if (!workflow_id || !sample_id || !stage_id) {
    throw new Error(`Invalid payload PK: empty component in "${pk}"`);
  }
  return { workflow_id, sample_id, stage_id };
}
